import { Component, Prop, h, Host } from '@stencil/core';

/**
 * Site-wide footer for SignNow brand with navigation menus, legal links, app store badges, social media links, and language switcher.
 * Delegates layout to footer-type-5 via pure slot pass-through.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @source footer
 * @slot logo - {required}, <me-logo> - Company logo
 * @slot menus - {required}, {multiple}, <footer-menu-type-1> - Footer navigation menu sections
 * @slot copyright - {required}, <me-column> - Copyright text column
 * @slot legal - {required}, <me-column> - Legal links column (privacy, terms, security)
 * @slot socials - <me-socials> - Social media icon links
 * @slot form-description - App store badges or newsletter description
 * @slot form - <form-free-trial-type-1> - Newsletter subscription form
 * @slot language-switcher - {required}, <me-language-switcher> - Language switcher
 * @example
 *   <sn-footer-type-1>
 *     <me-logo slot="logo" href="/" src="https://marketing-static.signnow.com/310/images/global/svg/signNow-logo-Colored.svg" alt="airSlate SignNow logo" width="124" height="42"></me-logo>
 *     <footer-menu-type-1 slot="menus">
 *       <me-text slot="title" variant="regular" weight="semi-bold">Company</me-text>
 *       <me-button slot="items" tag="a" variant="ghost" href="/about">About us</me-button>
 *       <me-button slot="items" tag="a" variant="ghost" href="/why-signnow">Why airSlate SignNow?</me-button>
 *     </footer-menu-type-1>
 *     <footer-menu-type-1 slot="menus">
 *       <me-text slot="title" variant="regular" weight="semi-bold">Resources</me-text>
 *       <me-button slot="items" tag="a" variant="ghost" href="/esignature">eSignature solution</me-button>
 *       <me-button slot="items" tag="a" variant="ghost" href="/features">Features</me-button>
 *     </footer-menu-type-1>
 *     <me-column slot="copyright">
 *       <me-text variant="small">© 2026 airSlate SignNow. All rights reserved.</me-text>
 *     </me-column>
 *     <me-column slot="legal">
 *       <me-button tag="a" href="/privacy_notice" variant="ghost">Privacy Notice</me-button>
 *       <me-button tag="a" href="/terms-of-service" variant="ghost">Terms of Service</me-button>
 *       <me-button tag="a" href="/security" variant="ghost">Security and compliance</me-button>
 *     </me-column>
 *     <me-socials slot="socials" items='[{"type":"linkedIn","href":"https://www.linkedin.com/company/signnow-esignature","label":"LinkedIn"}]'></me-socials>
 *     <form-free-trial-type-1 slot="form" button-variant="primary" mobile-layout="stacked" note="No credit card required" submit-text="Start free trial"></form-free-trial-type-1>
 *     <me-language-switcher slot="language-switcher" items='[{"current":true,"title":"English","link":"/en/"}]'></me-language-switcher>
 *   </sn-footer-type-1>
 */
@Component({
  tag: 'sn-footer-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNFooterType1 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <footer-type-5>
          <slot name="form-description"></slot>
          <slot name="form"></slot>
          <slot name="logo"></slot>
          <slot name="menus"></slot>
          <slot name="copyright"></slot>
          <slot name="legal"></slot>
          <slot name="socials"></slot>
          <slot name="language-switcher"></slot>
        </footer-type-5>
      </Host>
    );
  }
}
